import type { Facility } from '../../compliance-shared/types/common';
import type { FacilityConfig } from '../../compliance-shared/types/compliance';
import { computed, ref, watch } from 'vue';
import { INTEGRATION_TEST, UNIT_TEST } from '../../cross-app-shared/services/automated-testing.ts';
import { facilitiesRefresh } from '../../cross-app-frontend/facilities';
import { you, youRefresh } from '../../cross-app-frontend/users';
import { userConfirmedLoggedInPromise } from '../../cross-app-frontend/firebase';
import type { TenantID, FacilityGroupID, FacilityID } from '../../cross-app-shared/types/db';
import { actions } from '@/services/actions.ts';

export function newFacilityConfig(facilityID: Facility['facilityID']): FacilityConfig {
  return {
    facilityID,
    timeZone: '',
  };
}

export const facilitiesAccessible: {
  facilities: Facility[];
  facilitiesByID: Partial<Record<Facility['facilityID'], Facility>>;
  groups: Record<string, Facility[]>;
} = {
  facilities: [],
  facilitiesByID: {},
  groups: {},
};
export const facilitiesDetails: Record<Facility['facilityID'], Facility> = {};
const facilitiesLoaded = ref(false);

export let isSuperOrCompanyAdmin = false;
export let facilityCount = 0;
export let canManageUsers = false;

export const facilitiesInitialized =
  SERVER_SIDE || UNIT_TEST || INTEGRATION_TEST
    ? (async () => {
        const f: Facility = {
          tenantID: 0 as TenantID,
          tenantName: 'Test',
          facilityGroupID: 0 as FacilityGroupID,
          facilityID: 0 as FacilityID,
          name: 'Test Facility',
          abbr: 'TEST',
          groupName: 'Test',
          state: 'AZ',
          address: '1 Main St',
          city: 'Phoenix',
          zip: '85018',
          triID: '',
        };
        facilitiesAccessible.facilities.push(f);
        facilitiesAccessible.groups[f.groupName] = [f];
        facilitiesDetails[f.facilityID] = f;
      })()
    : (async () => {
        await userConfirmedLoggedInPromise;
        const [permissions] = await Promise.all([
          actions.facility.permissionsList(),
          // The cross-app list of facilities is required by Layout.vue and our own router (so it can guess at your
          // default tenant). Our own facilitiesDetails can end up a bit broader, as you are allowed to view tasks
          // in other facilities within the same tenant if you have a direct link:
          facilitiesRefresh(),
          youRefresh(),
        ]);
        canManageUsers = permissions.canManageUsers;
        isSuperOrCompanyAdmin = permissions.isSuperOrCompanyAdmin;

        const facilityIdsAccessible = new Set(permissions.facilitiesAccessible);

        for (const facility of permissions.facilitiesWithDetailsAvailable) {
          facilitiesDetails[facility.facilityID] = facility;

          if (facilityIdsAccessible.has(facility.facilityID)) {
            facilitiesAccessible.facilities.push(facility);

            if (!facilitiesAccessible.groups[facility.groupName]) {
              facilitiesAccessible.groups[facility.groupName] = [];
            }
            facilitiesAccessible.groups[facility.groupName].push(facility);
          }
        }

        facilitiesAccessible.facilities.sort((a, b) => {
          const tenantDiff = a.tenantID - b.tenantID;
          if (tenantDiff !== 0) {
            return tenantDiff;
          }

          const groupNameDiff = a.groupName.localeCompare(b.groupName);
          if (groupNameDiff !== 0) {
            return groupNameDiff;
          }

          return a.name.localeCompare(b.name);
        });

        facilityCount = facilitiesAccessible.facilities.length;
        facilitiesAccessible.facilitiesByID = Object.fromEntries(
          facilitiesAccessible.facilities.map((f) => [f.facilityID, f]),
        );

        facilitiesLoaded.value = true;
      })();

export const facilityLastVisited = ref(
  localStorage.getItem('compliance-facility-default')
    ? (Number(localStorage.getItem('compliance-facility-default')) as Facility['facilityID'])
    : null,
);
watch(facilityLastVisited, (newVal) => {
  if (newVal) {
    localStorage.setItem('compliance-facility-default', String(newVal));
  } else {
    localStorage.removeItem('compliance-facility-default');
  }
});

/**
 * Returns the default facility associated with the user, which is one of (checked in order):
 * 1. The last facility they visited (stored in localStorage), if accessible.
 * 2. The facility associated with them on their user document, if accessible.
 * 3. The first facility in their accessible facilities list.
 * 4. null if none of the above resolve to anything.
 */
export const userDefaultFacilityID = computed((): Facility['facilityID'] | null => {
  let result: Facility['facilityID'] | null = null;

  // This gets set in the router:
  const defaultSaved = facilityLastVisited.value;
  if (defaultSaved && facilitiesAccessible.facilities.find((f) => f.facilityID === defaultSaved)) {
    result = defaultSaved as Facility['facilityID'];
  } else if (you.value.primaryFacilityID) {
    result = facilitiesDetails[you.value.primaryFacilityID]?.facilityID || null;
    if (!facilitiesAccessible.facilities.find((f) => f.facilityID === result)) {
      result = null;
    }
  }

  if (!result && facilitiesLoaded.value && facilitiesAccessible.facilities.length > 0) {
    result = facilitiesAccessible.facilities[0].facilityID;
  }

  return result;
});
